/**
 * Requirements — prototype `vKanban()`, infra C.2.
 *
 * The entry point to the whole product: every requirement in flight, grouped by pipeline
 * stage, scoped to whoever is looking.
 *
 * Status here is READ-ONLY by design. It advances as a side effect of doing the work, so the
 * prototype disables status drag outright (`wireKanban` is an empty function) and the
 * contract has no endpoint to set it. The single manual decision is marking a sent quotation
 * Won or Rejected — purchaser/manager only, enforced server-side.
 *
 * FE/BE split: the role scope is a server-side filter (the board spans every requirement),
 * while the per-card age and the >30-day auto-archive are derived on the client from
 * timestamps (contract §3).
 */

import { useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { PageHead, Placeholder, Button, Card } from '../../components/ui'
import { useToast } from '../../components/Toast'
import { useSession } from '../../components/SessionProvider'
import { useDuplicateRequirement } from '../../lib/api/useDuplicateRequirement'
import { RejectQuotationModal } from './RejectQuotationModal'
import { useEnquiries } from '../../lib/api/useEnquiries'
import { useBoardReplies } from '../../lib/api/useBoardReplies'
import { NewEnquiryModal } from './NewEnquiryModal'
import { useDecideQuotation, useQuotationsByEnquiry } from '../../lib/api/useQuotation'
import { useSyncEnquiryChain } from '../../lib/api/g6Push'
import { RequirementsList } from './RequirementsList'
import { POLL_MS } from '../../lib/api/polling'
import { effectiveStatus } from '../../lib/format'
import { canSyncRequirement, confirmSyncMessage, confirmWonMessage, syncToast, wonToast } from '../../lib/wonDecision'
import { groupIntoColumns, routeForStatus, scopeForRole, ROLE_STRAPLINE } from '../../lib/kanban'
import { WATCHED_STATUSES } from '../../lib/boardReplies'
import { KanbanBoard } from './KanbanBoard'
import { RequirementFilterBar } from './RequirementFilterBar'
import {
  applyRequirementFilters,
  filterOptions,
  activeFilterCount,
  statusFilterFromQuery,
  EMPTY_FILTERS,
  type RequirementFilters,
} from '../../lib/requirementFilters'
import type { EnquirySummary } from '../../lib/types'

/**
 * Dev stand-in for the `enquiry.status` websocket event: a colleague advancing a requirement
 * should surface here without a manual refresh. Swap for the socket by invalidating the
 * `enquiries` key from the event handler and dropping this.
 */
const BOARD_POLL_MS = POLL_MS

export function RequirementsPage() {
  const navigate = useNavigate()
  const toast = useToast()
  const { role, user, canDecide } = useSession()

  const scope = useMemo(() => scopeForRole(role, user), [role, user])
  const enquiriesQuery = useEnquiries({ ...scope, limit: 200 }, { refetchInterval: BOARD_POLL_MS })

  /*
    Quotation numbers joined in from ONE `Quotation` read — the list wire carries none, and the
    board is searched by them (Akhil, 2026-09-15). Absent while that read is in flight, which
    only means a quotation-number search answers a moment later, never wrongly.
  */
  const quotationByEnquiry = useQuotationsByEnquiry({ refetchInterval: BOARD_POLL_MS })
  const loaded = useMemo(() => {
    const rows = enquiriesQuery.data?.data ?? []
    const index = quotationByEnquiry.data
    if (!index || index.size === 0) return rows
    return rows.map((r) => (r.quotation_id === null && index.has(r.id) ? { ...r, quotation_id: index.get(r.id) ?? null } : r))
  }, [enquiriesQuery.data, quotationByEnquiry.data])

  // Board for a dozen live requirements, list for hundreds — same rows, same filters.
  const [view, setView] = useState<'board' | 'list'>('board')

  /*
    Point 20. The filter narrows the rows BEFORE they are grouped, so the column counts describe
    exactly the cards underneath them — deriving the columns first and hiding cards afterwards is
    how a column comes to read "7" above three cards.

    Options come from `loaded`, not from `requirements`: a picker built from the already-filtered
    list would drop its own selected value the moment it was applied, and every other choice with
    it, so the filter could only ever be narrowed and never changed.
  */
  /*
    Seeded from `?status=` so the inbox strip's tiles (point 29) land on the board already showing
    what they counted. Read ONCE, as the initial value: after that the filter bar owns it, and
    re-reading the URL on every render would fight the user for control of their own filters.
  */
  const [searchParams] = useSearchParams()
  const [filters, setFilters] = useState<RequirementFilters>(() => ({
    ...EMPTY_FILTERS,
    status: statusFilterFromQuery(searchParams.get('status')),
  }))
  const options = useMemo(() => filterOptions(loaded), [loaded])
  const requirements = useMemo(() => applyRequirementFilters(loaded, filters), [loaded, filters])
  const filtering = activeFilterCount(filters) > 0

  const board = useMemo(() => groupIntoColumns(requirements), [requirements])

  /*
    Only the columns where "who hasn't come back?" is actionable. Scoping here rather than over the
    whole board keeps the `enquiry in [...]` filter short and leaves New / Won / Archived cards free
    of supplier chatter nobody is waiting on.
  */
  const watchedIds = useMemo(() => {
    const watched = new Set<string>(WATCHED_STATUSES)
    return board.columns
      .filter((column) => watched.has(column.status))
      .flatMap((column) => column.cards.map((card) => card.requirement.id))
  }, [board])

  const replies = useBoardReplies(watchedIds, { refetchInterval: BOARD_POLL_MS })
  /** The no-mail create dialog — see `NewEnquiryModal` for why it cannot be a bare button. */
  const [newEnquiry, setNewEnquiry] = useState(false)

  const decide = useDecideQuotation({
    onSuccess: (data, vars) => {
      toast(data.status === 'Won' ? wonToast(vars.enquiryId, data.push_warning ?? null) : `${vars.enquiryId} marked ${data.status}`)
    },
    onError: (error) => {
      toast(
        error.status === 403
          ? 'Only a purchaser or purchase manager can decide.'
          : error.status === 409
            ? 'Only a sent quotation can be marked Won or Rejected.'
            : error.message,
      )
    },
  })

  /*
    Sync (Akhil, 2026-10-24): re-send a won chain the ERP did not receive. The push on Won is
    best effort by design — a connector outage must not undo a Won — which left no second
    chance until this. Confirmed first: it sends data outward, the same reason the Won asks.
  */
  const sync = useSyncEnquiryChain({
    onDone: (enquiryId, warning) => toast(syncToast(enquiryId, warning)),
  })
  const syncRequirement = (requirement: EnquirySummary) => {
    if (!canSyncRequirement(requirement)) return
    if (window.confirm(confirmSyncMessage(requirement))) sync.mutate(requirement.id)
  }

  const openRequirement = (requirement: EnquirySummary) => {
    // Open where the work continues, not always at the start of the flow.
    const status = effectiveStatus(requirement.status, requirement.age_days)
    navigate(`/${routeForStatus(status)}/${requirement.id}`)
  }

  /*
    Duplicate (Akhil, 2026-09-16): a customer comes with the list another customer ordered. The
    whole requirement is copied — lines, and the quotation with every price, supplier, discount
    and tax — and the purchaser lands on the copy's revision page to add or remove lines. A
    confirm first: it creates two documents.
  */
  const duplicate = useDuplicateRequirement({
    onSuccess: ({ enquiryId, quotationId, warnings }) => {
      toast(`Copied as ${enquiryId} with quotation ${quotationId}`)
      warnings.forEach((w) => toast(w))
      navigate(`/revisions/${enquiryId}`)
    },
    onError: (error) => toast(error.message),
  })
  const duplicateRequirement = (requirement: EnquirySummary) => {
    if (!requirement.quotation_id) {
      toast('No quotation on this requirement yet — nothing priced to copy.')
      return
    }
    const ok = window.confirm(
      `Duplicate ${requirement.id} for ${requirement.customer_name}?\n\n` +
        `A new requirement is created with every line, and a new draft quotation priced exactly as ${requirement.quotation_id} — ` +
        `suppliers, costs, selling prices, discount and taxes. You can then add or remove lines on its revision page.`,
    )
    if (ok) duplicate.mutate(requirement.id)
  }

  // A rejection asks WHY first (2026-09-21) — reasons, competitors, a note — in `RejectQuotationModal`.
  const [rejecting, setRejecting] = useState<{ quotationId: string; enquiryId: string } | null>(null)
  const decideRequirement = (requirement: EnquirySummary, to: 'Won' | 'Rejected') => {
    if (!requirement.quotation_id) {
      toast('No quotation on this requirement yet.')
      return
    }
    if (to === 'Rejected') {
      setRejecting({ quotationId: requirement.quotation_id, enquiryId: requirement.id })
      return
    }
    // A Won asks first (2026-10-16): it is irreversible and now pushes the chain to G6.
    if (!window.confirm(confirmWonMessage(requirement))) return
    decide.mutate({ quotationId: requirement.quotation_id, enquiryId: requirement.id, to })
  }

  /* ---------- states ---------- */

  // A failed BACKGROUND refetch must not replace a working board — only a first load that
  // never arrived is a full-page error.
  const fatalError = enquiriesQuery.error && !enquiriesQuery.data
  const total = enquiriesQuery.data?.total ?? 0

  return (
    <>
      <PageHead help="requirements"
        title="Requirements"
        subtitle={ROLE_STRAPLINE[role]}
        actions={
          /*
            DISABLED, not removed — see `RfqPage.tsx`'s twin. `create_enquiry` requires `customer`
            AND `delivery_location` (measured), and this sends neither, so it could only produce a
            raw `MandatoryError` toast.
          */
          <Button
            variant="primary"
            title="For a requirement that arrived by phone or message"
            onClick={() => setNewEnquiry(true)}
          >
            ✚ New enquiry
          </Button>
        }
      />

      <div className="banner">
        🔒 Pipeline status is <b>read-only</b> — it advances automatically as work is done, so it can't be changed by
        mistake. The <b>only</b> manual decision is marking a sent quotation <b>Won</b> or <b>Rejected</b> (
        {canDecide ? 'you can do this' : 'purchaser / manager only'}). Quotations older than <b>30 days</b>{' '}
        auto-archive.
      </div>

      {enquiriesQuery.error && enquiriesQuery.data ? (
        <div className="banner" style={{ background: 'var(--warnbg)', borderColor: 'var(--warn)' }}>
          ⚠ Couldn't refresh the board just now — showing the last version. Retrying.
        </div>
      ) : null}

      {/*
        Said out loud, because the alternative is a board full of cards with no chip — which reads
        as "no RFQs have gone out" rather than "we could not check". A purchaser acting on that
        silence chases suppliers who have already replied.
      */}
      {newEnquiry ? <NewEnquiryModal onClose={() => setNewEnquiry(false)} /> : null}

      {replies.error ? (
        <div className="banner" style={{ background: 'var(--warnbg)', borderColor: 'var(--warn)' }}>
          ⚠ Couldn't check supplier replies just now, so cards show no reply status. {replies.error.message}
        </div>
      ) : replies.truncated ? (
        <div className="banner" style={{ background: 'var(--warnbg)', borderColor: 'var(--warn)' }}>
          ⚠ Too many RFQs to count in one go — the reply counts on these cards may be incomplete.
        </div>
      ) : null}

      {/* About the FETCH, not the filter — `loaded`, never `requirements`. Filtering to 4 of 200
          cards is not the same fact as the server holding more than this page returned, and the
          filter bar states its own count. */}
      {loaded.length < total ? (
        <div style={{ color: 'var(--muted)', fontSize: 12, marginBottom: 8 }}>
          Showing {loaded.length} of {total} requirements{filtering ? ' before filtering' : ''}.
        </div>
      ) : null}

      {board.unplaced.length > 0 ? (
        <div className="banner" style={{ background: 'var(--warnbg)', borderColor: 'var(--warn)' }}>
          ⚠ {board.unplaced.length} requirement(s) have a status this board doesn't recognise, and
          are shown under <strong>Open</strong> until it does:{' '}
          {/* The RAW status, not `c.effective` — `effective` is the folded value, so printing it
              would report every one of these as "(Open)" and read as a bug in the banner itself. */}
          {board.unplaced.map((c) => `${c.requirement.id} (${c.requirement.status_raw})`).join(', ')}
        </div>
      ) : null}

      {loaded.length > 0 ? (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, marginBottom: 6 }}>
            <Button size="sm" variant={view === 'board' ? 'primary' : undefined} onClick={() => setView('board')} title="Cards by stage">
              ▦ Board
            </Button>
            <Button size="sm" variant={view === 'list' ? 'primary' : undefined} onClick={() => setView('list')} title="One row per enquiry — for hundreds at once">
              ☰ List
            </Button>
          </div>
          <RequirementFilterBar
            filters={filters}
            options={options}
            showing={requirements.length}
            total={loaded.length}
            onChange={setFilters}
          />
        </>
      ) : null}

      {enquiriesQuery.isPending ? (
        <Placeholder>Loading requirements…</Placeholder>
      ) : fatalError ? (
        <Placeholder>
          <b>Could not load the board.</b> Check the mock backend is running (<b>npm run dev:all</b>).
          <div style={{ marginTop: 6 }}>{(enquiriesQuery.error as Error).message}</div>
        </Placeholder>
      ) : requirements.length === 0 && filtering ? (
        /* Distinct from "no requirements yet" below: there IS work, it just doesn't match. Telling
           someone their pipeline is empty when they have simply typed a customer name badly is a
           worse lie than any of the states around it. */
        <Card style={{ textAlign: 'center', padding: 34, borderStyle: 'dashed' }}>
          <div style={{ fontSize: 28 }}>🔍</div>
          <div style={{ fontWeight: 800, margin: '8px 0 4px' }}>No requirement matches these filters</div>
          <div style={{ color: 'var(--muted)', fontSize: 13 }}>
            {loaded.length} requirement{loaded.length === 1 ? ' is' : 's are'} loaded — none of them match.
          </div>
          <div style={{ marginTop: 12 }}>
            <Button onClick={() => setFilters(EMPTY_FILTERS)}>Clear filters</Button>
          </div>
        </Card>
      ) : requirements.length === 0 ? (
        <Card style={{ textAlign: 'center', padding: 34, borderStyle: 'dashed' }}>
          <div style={{ fontSize: 28 }}>🗂️</div>
          <div style={{ fontWeight: 800, margin: '8px 0 4px' }}>No requirements yet</div>
          <div style={{ color: 'var(--muted)', fontSize: 13 }}>
            {role === 'purchase_manager'
              ? 'Nothing is in flight. Start one with ✚ New enquiry, or open the Mail screen to turn a customer email into a requirement.'
              : role === 'sales'
                ? 'No requirements for your customers yet. Start one with ✚ New enquiry, or open the Mail screen.'
                : 'Nothing is assigned to you. A manager can reassign requirements from the RFQ Builder.'}
          </div>
        </Card>
      ) : view === 'list' ? (
        <RequirementsList
          requirements={requirements}
          onOpen={openRequirement}
          onDuplicate={duplicateRequirement}
          onSync={syncRequirement}
          syncingId={sync.isPending ? (sync.variables ?? null) : null}
          duplicatingId={duplicate.isPending ? (duplicate.variables ?? null) : null}
        />
      ) : (
        <KanbanBoard
          columns={board.columns}
          canDecide={canDecide}
          isDeciding={decide.isPending}
          decidingQuotationId={decide.isPending ? (decide.variables?.quotationId ?? null) : null}
          repliesByEnquiry={replies.byEnquiry}
          onOpen={openRequirement}
          onDecide={decideRequirement}
          onDuplicate={duplicateRequirement}
          onSync={syncRequirement}
          syncingId={sync.isPending ? (sync.variables ?? null) : null}
          duplicatingId={duplicate.isPending ? (duplicate.variables ?? null) : null}
        />
      )}
      {rejecting ? (
        <RejectQuotationModal
          quotationId={rejecting.quotationId}
          enquiryId={rejecting.enquiryId}
          busy={decide.isPending}
          onClose={() => setRejecting(null)}
          onReject={(lost) =>
            decide.mutate(
              { quotationId: rejecting.quotationId, enquiryId: rejecting.enquiryId, to: 'Rejected', lost },
              { onSuccess: () => setRejecting(null) },
            )
          }
        />
      ) : null}
    </>
  )
}
